<template>
  <div v-if="show" class="app-modal reports-detail-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog reports-detail-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reports-detail-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="reports-detail-modal-title">{{ resolvedTitle }}</h3>
          <p v-if="resolvedContextLine !== ''" class="reports-detail-modal__context">{{ resolvedContextLine }}</p>
          <p>{{ subtitle }}</p>
        </div>

        <button
          class="app-modal__close"
          type="button"
          :aria-label="detailModalText.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content reports-detail-modal__content">
        <div v-if="loading" class="reports-detail-modal__state">{{ detailModalText.loadingState }}</div>
        <div v-else-if="normalizedColumns.length === 0" class="reports-detail-modal__state">
          {{ detailModalText.emptyState }}
        </div>

        <div v-else class="app-table-wrap reports-detail-modal__table-wrap">
          <div class="app-table-scroll">
            <table class="app-data-table reports-detail-modal__table">
              <thead>
                <tr>
                  <th v-for="column in normalizedColumns" :key="column.key" :class="columnClass(column)">
                    {{ column.name }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(row, rowIndex) in normalizedRows" :key="`detail-row-${rowIndex}`">
                  <td
                    v-for="column in normalizedColumns"
                    :key="`detail-cell-${rowIndex}-${column.key}`"
                    :class="columnClass(column)"
                  >
                    <router-link
                      v-if="cellLinkTo(row, column)"
                      :to="cellLinkTo(row, column)"
                      :class="[
                        'reports-detail-link',
                        { 'reports-detail-link--provider': column.key === 'provider' },
                      ]"
                    >
                      {{ displayCellValue(row, column) }}
                    </router-link>
                    <span v-else>{{ displayCellValue(row, column) }}</span>
                  </td>
                </tr>

                <tr class="reports-detail-modal__grand-total-row">
                  <td
                    v-for="(column, columnIndex) in normalizedColumns"
                    :key="`detail-total-${column.key}`"
                    :class="columnClass(column)"
                  >
                    <span v-if="columnIndex === 0" class="app-strong">{{ detailModalText.grandTotalLabel }}</span>
                    <span v-else-if="column.key === 'amount'" class="app-strong">{{ formatAmount(grandTotal) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer class="app-modal__footer">
        <div class="app-modal__footer-right">
          <button type="button" class="btn btn-light" @click="emitClose">{{ detailModalText.closeButton }}</button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import { formatDateShort } from '@/utils/datePresentation';
import { REPORTS_SALES_TEXT, requireReportsSalesTextValue } from '@/composables/reports/sales/useReportsSalesPresentation';

export default {
  name: 'ReportsDetailModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    contextLine: {
      type: String,
      default: '',
    },
    columns: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    grandTotal: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close'],
  computed: {
    detailModalText() {
      const text = REPORTS_SALES_TEXT.detailModal;
      requireReportsSalesTextValue(text.defaultTitle, 'reports.sales.detailModal.defaultTitle');
      requireReportsSalesTextValue(text.closeAriaLabel, 'reports.sales.detailModal.closeAriaLabel');
      requireReportsSalesTextValue(text.loadingState, 'reports.sales.detailModal.loadingState');
      requireReportsSalesTextValue(text.emptyState, 'reports.sales.detailModal.emptyState');
      requireReportsSalesTextValue(text.grandTotalLabel, 'reports.sales.detailModal.grandTotalLabel');
      requireReportsSalesTextValue(text.closeButton, 'reports.sales.detailModal.closeButton');
      requireReportsSalesTextValue(text.columnFallbackPrefix, 'reports.sales.detailModal.columnFallbackPrefix');
      requireReportsSalesTextValue(text.emptyValueDash, 'reports.sales.detailModal.emptyValueDash');
      return text;
    },
    resolvedTitle() {
      const title = String(this.title || '').trim();
      return title === '' ? this.detailModalText.defaultTitle : title;
    },
    resolvedContextLine() {
      return String(this.contextLine || '').trim();
    },
    normalizedColumns() {
      return (Array.isArray(this.columns) ? this.columns : [])
        .map((column, index) => {
          const key = String(column?.key || '').trim();
          if (key === '') {
            return null;
          }

          return {
            key,
            name: String(column?.name || '').trim() || `${this.detailModalText.columnFallbackPrefix} ${index + 1}`,
            type: String(column?.type || '').trim().toLowerCase() || 'text',
          };
        })
        .filter(Boolean);
    },
    normalizedRows() {
      return (Array.isArray(this.rows) ? this.rows : []).filter((row) => row && typeof row === 'object');
    },
  },
  methods: {
    emitClose() {
      this.$emit('close');
    },
    formatAmount(value) {
      return formatCurrency(Number(value || 0), { currency: 'MXN' });
    },
    formatDate(value, { emptyValue = null } = {}) {
      const fallback = typeof emptyValue === 'string'
        ? emptyValue
        : this.detailModalText.emptyValueDash;

      return formatDateShort(value, { fallback });
    },
    displayCellValue(row, column) {
      const value = row?.[column.key];

      if (column.type === 'amount') {
        return this.formatAmount(value);
      }

      if (column.type === 'date') {
        const emptyValue = column.key === 'payment_date' ? '' : this.detailModalText.emptyValueDash;
        return this.formatDate(value, { emptyValue });
      }

      const text = String(value || '').trim();
      if (text === '' && ['invoice_reference', 'comprobante', 'type', 'payment', 'description'].includes(column.key)) {
        return '';
      }

      return text === '' ? this.detailModalText.emptyValueDash : text;
    },
    cellLinkTo(row, column) {
      if (!row || !column || typeof row !== 'object') {
        return null;
      }

      if (column.key === 'name' || column.key === 'description') {
        const saleId = Number(row?.sale_id || 0);
        if (Number.isInteger(saleId) && saleId > 0) {
          return {
            name: 'saleDetail',
            params: { saleId },
          };
        }

        const customerId = Number(row?.customer_id || 0);
        if (Number.isInteger(customerId) && customerId > 0) {
          return {
            name: 'customerDetail',
            params: { customerId },
          };
        }
      }

      if (column.key === 'invoice_reference' || column.key === 'comprobante') {
        const invoiceId = Number(row?.invoice_id || 0);
        if (Number.isInteger(invoiceId) && invoiceId > 0) {
          return {
            name: 'invoiceDetail',
            params: { invoiceId },
          };
        }
      }

      if (column.key === 'provider') {
        const supplierId = Number(row?.provider_id || 0);
        if (Number.isInteger(supplierId) && supplierId > 0) {
          return {
            name: 'supplierDetail',
            params: { supplierId },
          };
        }
      }

      return null;
    },
    columnClass(column) {
      return {
        'app-text-right': column.type === 'amount',
      };
    },
  },
};
</script>

<style scoped>
.reports-detail-modal__dialog {
  display: flex;
  flex-direction: column;
  max-width: 1120px;
  height: auto;
  max-height: calc(100vh - 48px);
}

.reports-detail-modal__content {
  flex: 1 1 auto;
  min-height: 280px;
  overflow: auto;
  padding: 24px;
}

.reports-detail-modal__context {
  color: #334b68;
  font-weight: 600;
  margin: 6px 0 0;
}

.reports-detail-modal__state {
  align-items: center;
  color: #4b5d78;
  display: flex;
  justify-content: center;
  min-height: 220px;
  text-align: center;
}

.reports-detail-modal__table-wrap {
  border: 1px solid #d8e2ef;
  border-radius: 10px;
  max-height: 100%;
  overflow: hidden;
}

.reports-detail-modal__table-wrap .app-table-scroll {
  max-height: calc(100vh - 320px);
  overflow: auto;
}

.reports-detail-modal__table {
  width: 100%;
}

.reports-detail-modal__grand-total-row td {
  background: rgba(11, 63, 114, 0.12);
  border-top: 2px solid rgba(11, 63, 114, 0.55);
  color: #0b3f72;
  font-weight: 700;
}

.reports-detail-link {
  color: #0070cd;
  text-decoration: none;
}

.reports-detail-link:hover,
.reports-detail-link:focus-visible {
  text-decoration: underline;
}

.reports-detail-link--provider {
  font-weight: 700;
  text-decoration: none;
}

.reports-detail-link--provider:hover,
.reports-detail-link--provider:focus-visible {
  text-decoration: none;
}

@media (max-width: 767px) {
  .reports-detail-modal__dialog {
    max-width: calc(100vw - 20px);
  }

  .reports-detail-modal__table-wrap .app-table-scroll {
    max-height: calc(100vh - 280px);
  }
}
</style>
